import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { GrantPromptDialog } from '../components/portal/GrantPromptDialog';

const show = (v) => (v && typeof v === 'object' ? JSON.stringify(v) : v ?? '—');

export default function PortalChangeRequests() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('pending');
  const [reasons, setReasons] = useState({});
  const [prompt, setPrompt] = useState(null);
  const load = useCallback(() => api.get('/api/portal-changes', { params: { status } }).then(({ data }) => setRows(data)), [status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    try {
      const { data } = await api.post(`/api/portal-changes/${r.id}/approve`);
      toast.success('Approved and applied');
      if (data.new_client_id) setPrompt({ clientId: data.new_client_id, creator: r.user_id });
      load();
    } catch (e) { toast.error(e.response?.data?.detail === 'STEP_UP_REQUIRED' ? "Confirm it's you, then approve again" : e.response?.data?.detail || 'Could not approve'); }
  };
  const reject = (r) => api.post(`/api/portal-changes/${r.id}/reject`, { reason: reasons[r.id] }).then(() => { toast.success('Rejected'); load(); })
    .catch((e) => toast.error(e.response?.data?.detail || 'Could not reject'));

  return (
    <div className="p-6 lg:p-10 space-y-8" data-testid="portal-change-requests-page">
      <div className="flex items-end justify-between gap-4">
        <h1 className="text-4xl sm:text-5xl font-headings font-bold">Client change requests</h1>
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pcr-status-filter">
          {['pending', 'awaiting_verification', 'approved', 'rejected', 'applied', 'all'].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div className="rounded-xl border border-border divide-y divide-border bg-card">
        {rows.length === 0 && <p className="p-6 text-sm text-muted-foreground" data-testid="pcr-empty">Nothing here.</p>}
        {rows.map((r) => (
          <div key={r.id} className="p-4 space-y-2" data-testid={`pcr-row-${r.id}`}>
            <div className="flex items-center gap-2 text-sm"><b>{r.client_name}</b><Badge variant="outline">{r.kind.replace('_', ' ')}</Badge><Badge>{r.status}</Badge>
              <span className="text-muted-foreground">{new Date(r.created_at).toLocaleString('en-AU')}</span></div>
            {r.changes.map((c) => <p key={c.field} className="text-sm">{c.label}: <span className="text-muted-foreground">{show(c.old)}</span> → {show(c.new)}</p>)}
            {r.status === 'pending' && (
              <div className="flex gap-2 max-w-xl">
                <Button size="sm" onClick={() => approve(r)} data-testid={`pcr-approve-${r.id}`}>Approve</Button>
                <Input placeholder="Reason (for rejection)" value={reasons[r.id] || ''} onChange={(e) => setReasons({ ...reasons, [r.id]: e.target.value })} data-testid={`pcr-reason-${r.id}`} />
                <Button size="sm" variant="outline" onClick={() => reject(r)} data-testid={`pcr-reject-${r.id}`}>Reject</Button>
              </div>
            )}
            {r.reason && <p className="text-xs text-destructive">Reason: {r.reason}</p>}
          </div>
        ))}
      </div>
      <GrantPromptDialog open={!!prompt} clientId={prompt?.clientId} creatorUserId={prompt?.creator} onClose={() => setPrompt(null)} />
    </div>
  );
}
